import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TAG_COLORS,
  FOLDER_ICON_LIST,
  FolderIcon,
  DEFAULT_FOLDER_COLOR,
  DEFAULT_FOLDER_ICON,
  pickNextColor,
  suggestIconForName,
} from "@/lib/folderStyle";
import { Check } from "lucide-react";
import { buildFolderTree } from "@/components/TagPicker";

function flatten(tree, depth = 0, acc = []) {
  for (const n of tree) {
    acc.push({ id: n.id, name: n.name, depth });
    flatten(n.children, depth + 1, acc);
  }
  return acc;
}

export default function FolderDialog({
  open,
  onOpenChange,
  folders,
  defaultParentId,
  initialFolder, // edit mode
  onCreate,
  onUpdate,
}) {
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState(null);
  const [color, setColor] = useState(DEFAULT_FOLDER_COLOR);
  const [icon, setIcon] = useState(DEFAULT_FOLDER_ICON);
  // Track whether the user has manually overridden the auto-suggestions
  const [iconTouched, setIconTouched] = useState(false);
  const [colorTouched, setColorTouched] = useState(false);

  useEffect(() => {
    if (open) {
      if (initialFolder) {
        setName(initialFolder.name || "");
        setParentId(initialFolder.parent_id ?? null);
        setColor(initialFolder.color || DEFAULT_FOLDER_COLOR);
        setIcon(initialFolder.icon || DEFAULT_FOLDER_ICON);
        setIconTouched(true);
        setColorTouched(true);
      } else {
        setName("");
        setParentId(defaultParentId ?? null);
        // Auto-pick a colour that hasn't been used by other folders yet
        const used = folders.map((f) => f.color).filter(Boolean);
        setColor(pickNextColor(used, folders.length));
        setIcon(DEFAULT_FOLDER_ICON);
        setIconTouched(false);
        setColorTouched(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultParentId, initialFolder]);

  // While typing the name, auto-suggest an icon (only if user hasn't manually picked one)
  useEffect(() => {
    if (!open || iconTouched) return;
    const suggested = suggestIconForName(name);
    if (suggested) setIcon(suggested);
  }, [name, open, iconTouched]);

  // Exclude self & descendants from parent options (can't nest under own descendants)
  const flat = flatten(buildFolderTree(folders));
  const filteredFlat = initialFolder
    ? flat.filter((f) => f.id !== initialFolder.id) // simple: exclude self
    : flat;

  const submit = () => {
    if (!name.trim()) return;
    const payload = {
      name: name.trim(),
      parent_id: parentId,
      color,
      icon,
    };
    if (initialFolder && onUpdate) {
      onUpdate(initialFolder.id, payload);
    } else {
      onCreate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-[#111111] border border-white/10 text-white max-h-[90vh] overflow-y-auto thin-scroll"
        data-testid="folder-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-wider flex items-center gap-3">
            <span
              className="h-9 w-9 rounded-lg border flex items-center justify-center"
              style={{
                borderColor: color,
                background: `${color}1F`,
                boxShadow: `0 0 12px ${color}55`,
                color,
              }}
            >
              <FolderIcon name={icon} className="h-5 w-5" />
            </span>
            {initialFolder ? "Edit Folder" : "New Folder"}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-white/50">
            Folders organise your tags. Tags within a folder can be combined freely — when filtering
            you can pick one or many, and use the per-folder{" "}
            <span className="text-[#00e5ff]">And / Or</span> toggle to choose how multiple tags
            combine.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
              Folder name
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="e.g. Gender, Age, Attributes"
              className="bg-transparent border-white/20 neon-input h-12 font-mono"
              data-testid="folder-name-input"
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
              Colour
            </Label>
            <div className="grid grid-cols-6 gap-2" data-testid="folder-color-picker">
              {TAG_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => {
                    setColor(c.value);
                    setColorTouched(true);
                  }}
                  title={c.name}
                  aria-label={c.name}
                  data-testid={`folder-color-${c.name.toLowerCase()}`}
                  className="relative h-9 w-9 rounded-full transition-transform hover:scale-110"
                  style={{
                    background: c.value,
                    boxShadow:
                      color === c.value
                        ? `0 0 0 2px #0a0a0a, 0 0 0 4px ${c.value}, 0 0 14px ${c.value}`
                        : `0 0 8px ${c.value}66`,
                  }}
                >
                  {color === c.value && (
                    <Check
                      className="h-4 w-4 absolute inset-0 m-auto"
                      style={{ color: "#0a0a0a" }}
                      strokeWidth={3}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
              Icon
            </Label>
            <div className="grid grid-cols-10 gap-2" data-testid="folder-icon-picker">
              {FOLDER_ICON_LIST.map((iconName) => {
                const active = icon === iconName;
                return (
                  <button
                    key={iconName}
                    type="button"
                    onClick={() => {
                      setIcon(iconName);
                      setIconTouched(true);
                    }}
                    aria-label={iconName}
                    data-testid={`folder-icon-${iconName.toLowerCase()}`}
                    className={`h-9 w-9 rounded-md border flex items-center justify-center transition-all ${
                      active
                        ? "text-[#0a0a0a]"
                        : "text-white/70 border-white/15 hover:border-white/40"
                    }`}
                    style={
                      active
                        ? {
                            background: color,
                            borderColor: color,
                            boxShadow: `0 0 10px ${color}88`,
                          }
                        : undefined
                    }
                  >
                    <FolderIcon name={iconName} className="h-4 w-4" />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
              Parent
            </Label>
            <Select
              value={parentId || "__root__"}
              onValueChange={(v) => setParentId(v === "__root__" ? null : v)}
            >
              <SelectTrigger
                className="bg-transparent border-white/20 text-white h-12 font-mono"
                data-testid="folder-parent-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111111] border border-white/10 text-white">
                <SelectItem value="__root__" className="font-mono">
                  — Top level —
                </SelectItem>
                {filteredFlat.map((f) => (
                  <SelectItem key={f.id} value={f.id} className="font-mono">
                    {"  ".repeat(f.depth)}
                    {f.depth > 0 ? "↳ " : ""}
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-3">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="btn-ghost h-11 px-5"
            data-testid="folder-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!name.trim()}
            className="btn-neon h-11 px-6"
            data-testid="folder-create-button"
          >
            {initialFolder ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
